import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  BarChart3,
  CreditCard,
  Inbox,
  ListTodo,
  Ticket,
  TrendingUp,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';
import { apiFetch } from '../../lib/api';
import { getSkuById } from '../../lib/pricebook/pricebook';
import OnboardingTasksPanel from '../../components/onboarding/OnboardingTasksPanel';
import SchedulePage from './SchedulePage';
import {
  SmallGrayPanel,
  backendPalette,
} from '../../components/backend/BackendCardSystem';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const overviewAccent = '#e0a15e';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  max-width: 1400px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Greeting = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  color: ${overviewAccent};
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.gray700};
  margin: 0;
  font-size: 1.25rem;
`;

const QuickActions = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const ActionCard = styled(motion.button)`
  background: transparent;
  border: 1.5px solid rgba(150, 150, 150, 0.7);
  border-radius: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)};
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  &:hover {
    border-color: rgba(190, 190, 190, 0.95);
    background: rgba(255, 255, 255, 0.02);
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.22);
  }
`;

const ActionIcon = styled.div`
  width: 48px;
  height: 48px;
  background: transparent;
  border: 1px solid rgba(150, 150, 150, 0.78);
  border-radius: ${({ theme }) => theme.spacing(1.5)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(232, 236, 244, 0.95);
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 1.9;
  }
`;

const ActionContent = styled.div`
  flex: 1;
`;

const ActionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(0.5)};
  color: ${overviewAccent};
  font-size: 1.1rem;
`;

const ActionDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.9rem;
`;

const InsightsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: start;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const StatsPanel = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const StatsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCardGray = styled(SmallGrayPanel)`
  min-height: 102px;
  display: grid;
  align-content: center;
  gap: 6px;
`;

const MetricValue = styled.div<{ $compact?: boolean }>`
  position: relative;
  z-index: 1;
  color: ${backendPalette.black};
  font-weight: 900;
  line-height: 1;
  font-size: ${({ $compact }) => ($compact ? '1.25rem' : '2rem')};
`;

const MetricLabel = styled.div`
  position: relative;
  z-index: 1;
  color: rgba(26, 26, 26, 0.82);
  font-size: 0.78rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-weight: 800;
`;

const StatsMessage = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.95rem;
`;

const TasksSquare = styled(OnboardingTasksPanel)`
  width: 100%;
  justify-self: end;
`;

const TasksPanelFrame = styled.div`
  width: 100%;
  min-height: 0;
  height: clamp(340px, 48vh, 560px);
`;

const ScheduleEmbed = styled.div`
  width: 100%;

  > section {
    max-width: 100%;
    margin: 0;
    padding: 0;
  }
`;

type QuickAction = {
  icon: LucideIcon;
  title: string;
  description: string;
  path: string;
};

type OverviewStat = {
  title: string;
  value: string;
  compact?: boolean;
};

type TeamOverviewAgent = {
  uid: string | null;
  email: string | null;
  withinTargetCount: number;
  outsideTargetCount: number;
  availableMinutes: number;
  scheduledMinutes: number;
};

type TeamOverviewResponse = {
  summary?: {
    activeProjects?: number | null;
  };
  distributions?: {
    agents?: TeamOverviewAgent[];
  };
};

type OperatorDetailsResponse = {
  team?: string | null;
  openTicketStats?: {
    totalOpen?: number | null;
  };
};

type AdminUsersResponse = {
  users?: Array<{
    id?: string | null;
    banned?: boolean | null;
  }>;
};

type AdminOrganizationsResponse = {
  organizations?: Array<{
    id?: string | null;
    primaryServiceActive?: boolean | null;
    createdAt?: string | null;
  }>;
};

type OrganizationDetailResponse = {
  serviceContracts?: Array<{
    status?: string | null;
    tierSkuId?: string | null;
  }>;
};

const AGENT_STAT_SKELETON: OverviewStat[] = [
  { title: 'Open Tickets', value: '--' },
  { title: 'Team', value: '--', compact: true },
  { title: 'Capacity', value: '--', compact: true },
  { title: 'Performance', value: '--' },
];

const ADMIN_STAT_SKELETON: OverviewStat[] = [
  { title: 'Total Monthly Revenue', value: '--', compact: true },
  { title: 'New Organizations (This Month)', value: '--', compact: true },
  { title: 'Company Size', value: '--' },
  { title: 'Total Projects', value: '--' },
];

function formatCurrencyFromCents(amountCents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amountCents / 100);
}

function formatFreeCapacity(minutes: number): string {
  const hours = minutes / 60;
  if (hours >= 10) return `${Math.round(hours)}h free`;
  return `${hours.toFixed(1)}h free`;
}

async function fetchAllUsersByRole(role: 'admin' | 'agent') {
  const seen = new Map<string, { id?: string | null; banned?: boolean | null }>();

  for (let page = 0; page < 50; page += 1) {
    const response = await apiFetch<AdminUsersResponse>(
      `/api/admin/users?role=${role}&page=${page}`,
    );
    const users = Array.isArray(response?.users) ? response.users : [];

    for (const user of users) {
      const id = String(user?.id ?? '').trim();
      if (!id) continue;
      seen.set(id, user);
    }

    if (users.length < 20) {
      break;
    }
  }

  return Array.from(seen.values());
}

async function deriveMonthlyRevenueFromOrganizations(
  orgs: Array<{ id: string }>,
): Promise<number> {
  if (!orgs.length) return 0;

  const detailResponses = await Promise.allSettled(
    orgs.map((org) =>
      apiFetch<OrganizationDetailResponse>(
        `/api/admin/organizations/${encodeURIComponent(org.id)}`,
      ),
    ),
  );

  return detailResponses.reduce((sum, result) => {
    if (result.status !== 'fulfilled') return sum;

    const contracts = Array.isArray(result.value?.serviceContracts)
      ? result.value.serviceContracts
      : [];

    return (
      sum +
      contracts.reduce((contractSum, contract) => {
        const status = String(contract?.status ?? '').trim().toLowerCase();
        const tierSkuId = String(contract?.tierSkuId ?? '').trim();
        if (status !== 'active' || !tierSkuId) return contractSum;

        const sku = getSkuById(tierSkuId);
        if (!sku || sku.billingType !== 'recurring') return contractSum;
        return contractSum + sku.amountCents;
      }, 0)
    );
  }, 0);
}

export default function OverviewPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [roleStats, setRoleStats] = useState<OverviewStat[]>(
    role === 'agent' ? AGENT_STAT_SKELETON : ADMIN_STAT_SKELETON,
  );
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      if (!role || (role !== 'admin' && role !== 'agent')) {
        if (!cancelled) {
          setRoleStats([]);
          setStatsError(null);
          setStatsLoading(false);
        }
        return;
      }
      if (!user?.uid) return;

      if (!cancelled) {
        setStatsLoading(true);
        setStatsError(null);
        setRoleStats(role === 'agent' ? AGENT_STAT_SKELETON : ADMIN_STAT_SKELETON);
      }

      try {
        if (role === 'agent') {
          const [details, overview] = await Promise.all([
            apiFetch<OperatorDetailsResponse>(
              `/api/admin/users/${encodeURIComponent(user.uid)}`,
            ),
            apiFetch<TeamOverviewResponse>('/api/team/overview?timeframe=month'),
          ]);

          const agents = Array.isArray(overview?.distributions?.agents)
            ? overview.distributions.agents
            : [];
          const normalizedEmail = String(user.email ?? '').trim().toLowerCase();
          const agentEntry =
            agents.find((entry) => String(entry.uid ?? '').trim() === user.uid) ??
            agents.find(
              (entry) =>
                String(entry.email ?? '').trim().toLowerCase() === normalizedEmail,
            ) ??
            null;

          const trackedTickets =
            Number(agentEntry?.withinTargetCount ?? 0) +
            Number(agentEntry?.outsideTargetCount ?? 0);
          const freeMinutes = Math.max(
            0,
            Number(agentEntry?.availableMinutes ?? 0) -
              Number(agentEntry?.scheduledMinutes ?? 0),
          );

          if (!cancelled) {
            setRoleStats([
              {
                title: 'Open Tickets',
                value: String(details?.openTicketStats?.totalOpen ?? 0),
              },
              {
                title: 'Team',
                value: String(details?.team ?? '').trim() || 'Unassigned',
                compact: true,
              },
              {
                title: 'Capacity',
                value: agentEntry ? formatFreeCapacity(freeMinutes) : '--',
                compact: true,
              },
              {
                title: 'Performance',
                value:
                  trackedTickets > 0
                    ? `${Math.round(
                        (Number(agentEntry?.withinTargetCount ?? 0) /
                          trackedTickets) *
                          100,
                      )}%`
                    : '--',
              },
            ]);
          }

          return;
        }

        const [overview, admins, agents, orgResponse] = await Promise.all([
          apiFetch<TeamOverviewResponse>('/api/team/overview?timeframe=month'),
          fetchAllUsersByRole('admin'),
          fetchAllUsersByRole('agent'),
          apiFetch<AdminOrganizationsResponse>('/api/admin/organizations'),
        ]);

        const organizations = Array.isArray(orgResponse?.organizations)
          ? orgResponse.organizations
              .map((org) => ({
                id: String(org?.id ?? '').trim(),
                primaryServiceActive: org?.primaryServiceActive === true,
                createdAt: String(org?.createdAt ?? '').trim(),
              }))
              .filter((org) => Boolean(org.id))
          : [];

        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const newOrganizationsThisMonth = organizations.reduce((count, org) => {
          if (!org.createdAt) return count;
          const createdAt = new Date(org.createdAt);
          if (Number.isNaN(createdAt.getTime())) return count;
          return createdAt >= monthStart && createdAt < nextMonthStart ? count + 1 : count;
        }, 0);

        const totalRevenueCents = await deriveMonthlyRevenueFromOrganizations(
          organizations.filter((org) => org.primaryServiceActive),
        );
        const companySize =
          admins.filter((member) => member?.banned !== true).length +
          agents.filter((member) => member?.banned !== true).length;

        if (!cancelled) {
          setRoleStats([
            {
              title: 'Total Monthly Revenue',
              value: formatCurrencyFromCents(totalRevenueCents),
              compact: true,
            },
            {
              title: 'New Organizations (This Month)',
              value: String(newOrganizationsThisMonth),
              compact: true,
            },
            {
              title: 'Company Size',
              value: String(companySize),
            },
            {
              title: 'Total Projects',
              value: String(overview?.summary?.activeProjects ?? 0),
            },
          ]);
        }
      } catch (error) {
        if (!cancelled) {
          setStatsError(
            error instanceof Error
              ? error.message
              : 'Failed to load overview stats.',
          );
          setRoleStats(role === 'agent' ? AGENT_STAT_SKELETON : ADMIN_STAT_SKELETON);
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
        }
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [role, user?.email, user?.uid]);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getUserName = () => {
    if (!user?.email) return 'there';
    return user.email.split('@')[0];
  };

  const getQuickActions = () => {
    const actions: QuickAction[] = [];

    if (role === 'admin' || role === 'agent') {
      actions.push({
        icon: ListTodo,
        title: 'View Ticket Queue',
        description: 'See all pending tickets',
        path: '/dashboard/ticket-queue',
      });
    }

    if (role === 'admin' || role === 'agent' || role === 'client') {
      actions.push({
        icon: Ticket,
        title: 'Create New Ticket',
        description: 'Submit a new request',
        path: '/dashboard/tickets/new',
      });
    }

    if (role === 'admin') {
      actions.push({
        icon: Users,
        title: 'Open CRM',
        description: 'View and manage people records',
        path: '/dashboard/users',
      });
      actions.push({
        icon: BarChart3,
        title: 'View Analytics',
        description: 'Check performance metrics',
        path: '/dashboard/analytics',
      });
    }

    if (role === 'agent') {
      actions.push({
        icon: Inbox,
        title: 'List',
        description: 'View your current tickets',
        path: '/dashboard/list',
      });
      actions.push({
        icon: Wallet,
        title: 'Earnings',
        description: 'Track your payouts',
        path: '/dashboard/earnings',
      });
    }

    if (role === 'client') {
      actions.push({
        icon: TrendingUp,
        title: 'Project Status',
        description: 'Track your projects',
        path: '/dashboard/my-projects',
      });
      actions.push({
        icon: CreditCard,
        title: 'Billing',
        description: 'Manage payments',
        path: '/dashboard/billing',
      });
    }

    return actions;
  };

  return (
    <Wrap>
      <Header>
        <Greeting>
          {getGreeting()}, {getUserName()}!
        </Greeting>
        <Subtitle>
          {role === 'admin' && "Here's your agency overview"}
          {role === 'agent' && "Here's your daily dashboard"}
          {role === 'client' && "Welcome to your project hub"}
        </Subtitle>
      </Header>

      <QuickActions variants={staggerContainer} initial="hidden" animate="visible">
        {getQuickActions().map((action, index) => {
          const Icon = action.icon;
          return (
            <ActionCard
              key={`${action.title}-${index}`}
              variants={fadeInUp}
              onClick={() => navigate(action.path)}
            >
              <ActionIcon>
                <Icon aria-hidden="true" />
              </ActionIcon>
              <ActionContent>
                <ActionTitle>{action.title}</ActionTitle>
                <ActionDescription>{action.description}</ActionDescription>
              </ActionContent>
            </ActionCard>
          );
        })}
      </QuickActions>

      <InsightsRow>
        <StatsPanel>
          {statsError ? <StatsMessage>{statsError}</StatsMessage> : null}
          <StatsGrid variants={staggerContainer} initial="hidden" animate="visible">
            {roleStats.map((stat, index) => (
              <MetricCardGray key={`${stat.title}-${index}`}>
                <MetricValue $compact={stat.compact}>
                  {statsLoading ? '--' : stat.value}
                </MetricValue>
                <MetricLabel>{stat.title}</MetricLabel>
              </MetricCardGray>
            ))}
          </StatsGrid>
        </StatsPanel>

        <TasksPanelFrame>
          <TasksSquare
            square
            tone="green"
            externalHeader
            fillHeight
            subtleTaskHover
            accentColor={overviewAccent}
          />
        </TasksPanelFrame>
      </InsightsRow>

      <ScheduleEmbed>
        <SchedulePage />
      </ScheduleEmbed>
    </Wrap>
  );
}
